import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Clock, Trash2, Bold, Italic, Quote, Maximize, Minimize, Type, Download, FileText, Copy, Check, Reply, ThumbsUp, Heart, Smile, ChevronDown, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../services/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { parseTimestamp, extractTimestamps, formatSecondsToTime } from '../utils/timestampParser';
import { useAudio, useAudioSeek } from '../contexts/AudioContext';
import { useShareLink } from '../contexts/ShareLinkContext';
import { useDevicePerformance } from '../hooks/useDevicePerformance';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' 
  : 'http://localhost:7777';

interface Comment {
  id: string;
  sessionId: string;
  userEmail: string;
  userName: string;
  content: string;
  timestamp?: Date;
  created_at?: string | Date;
  createdAt?: string | Date;
  timestampSeconds?: number;
  parentCommentId?: string;
  isAnonymous?: boolean;
  anonymousSessionId?: string;
  type: 'comment' | 'pinned_message';
  pinnedContent?: {
    originalMessage: string;
    model: string;
    timestamp: Date;
  };
  replies?: Comment[];
  reactions?: CommentReaction[];
  displayName?: string;
  avatarColor?: string;
}

interface CommentReaction {
  emoji: string;
  count: number;
  userReacted: boolean;
  users?: string;
}

interface TTMLFile {
  id: string;
  content: string;
  commentId: string;
  createdAt: Date;
  filename?: string;
}

interface CommentsProps {
  sessionId: string | null;
  token: string | null;
  isPublicView?: boolean;
  isInline?: boolean;
  onCommentsChange?: (comments: Comment[]) => void;
  pendingTimestamp?: number | null;
  selectedCommentId?: string | null;
  onClose?: () => void;
  initialComments?: Comment[];  // Pre-loaded comments from CDN bundle
  disablePolling?: boolean;     // Disable polling for static/CDN content
}

const CommentsComponent: React.FC<CommentsProps> = ({ sessionId, token, isPublicView = false, isInline = false, onCommentsChange, pendingTimestamp, selectedCommentId, onClose, initialComments, disablePolling = false }) => {
  const { user } = useAuth();
  const { isDarkMode } = useDarkMode();
  const { seekTo } = useAudioSeek();
  const { currentTime: playheadTime } = useAudio();
  const seekToRef = useRef(seekTo);
  const { permissions: sharePermissions } = useShareLink();
  const { isCompactTouchLayout } = useDevicePerformance();
  
  // Update ref when seekTo changes (which should be rare)
  useEffect(() => {
    seekToRef.current = seekTo;
  }, [seekTo]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isMobilePanel = isCompactTouchLayout;
  const [comments, setComments] = useState<Comment[]>(initialComments || []);
  const [panelHeight, setPanelHeight] = useState(() => (
    typeof window !== 'undefined' ? window.innerHeight - 200 : 500
  ));
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartHeight, setDragStartHeight] = useState(0);
  const [isInputCollapsed, setIsInputCollapsed] = useState(false);
  const [panelPosition, setPanelPosition] = useState(() => ({
    x: typeof window !== 'undefined' ? window.innerWidth - 500 : 0,
    y: 100,
  }));
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  
  // Debug: Log when component re-renders with more details
  useEffect(() => {
    console.log('[Comments] Component rendered at', new Date().toISOString(), {
      sessionId,
      isInline,
      pendingTimestamp,
      selectedCommentId,
      hasOnCommentsChange: !!onCommentsChange,
      hasOnClose: !!onClose,
      timestamp: Date.now()
    });
  }, [sessionId, isInline, pendingTimestamp, selectedCommentId]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isIdle, setIsIdle] = useState(false);
  const [showFormattingHelp, setShowFormattingHelp] = useState(false);
  const [anonName, setAnonName] = useState(() => {
    // Load saved anonymous name from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem('anonymousName') || '';
    }
    return '';
  });
  const [copiedCommentId, setCopiedCommentId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyingToComment, setReplyingToComment] = useState<Comment | null>(null);
  const [reactions, setReactions] = useState<Record<string, CommentReaction[]>>({});
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const focusTimerRef = useRef<number | null>(null);
  const selectedCommentTimerRef = useRef<number | null>(null);
  const isComposerFocusedRef = useRef(false);
  
  // Get or create anonymous session ID
  const getAnonymousSessionId = () => {
    if (typeof window === 'undefined') return null;
    
    let sessionId = localStorage.getItem('anonymousSessionId');
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('anonymousSessionId', sessionId);
    }
    return sessionId;
  };
  
  // Save anonymous name to localStorage when it changes
  useEffect(() => {
    if (typeof window !== 'undefined' && anonName) {
      localStorage.setItem('anonymousName', anonName);
    }
  }, [anonName]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousCommentsLength = useRef<number>(0);
  const isIdleRef = useRef<boolean>(false);

  // Exponential backoff for polling - reduces server load when idle
  const BASE_POLL_INTERVAL = 30000; // 30 seconds
  const MAX_POLL_INTERVAL = 300000; // 5 minutes max
  const currentPollIntervalRef = useRef<number>(BASE_POLL_INTERVAL);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isMobilePanel) {
      setPanelPosition({ x: 0, y: 0 });
      return undefined;
    }

    const handleResize = () => {
      setPanelHeight((prevHeight) => {
        const minHeight = 200;
        const maxHeight = Math.max(minHeight, window.innerHeight - 100);
        return Math.max(minHeight, Math.min(maxHeight, prevHeight));
      });
      setPanelPosition((prevPosition) => {
        const maxX = Math.max(0, window.innerWidth - 500);
        const maxY = Math.max(0, window.innerHeight - panelHeight);
        return {
          x: Math.max(0, Math.min(maxX, prevPosition.x)),
          y: Math.max(0, Math.min(maxY, prevPosition.y)),
        };
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobilePanel, panelHeight]);
  
  // Keep isIdle ref in sync
  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);

  const clearScheduledFocus = useCallback(() => {
    if (focusTimerRef.current !== null) {
      window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }
  }, []);

  const focusComposer = useCallback((selectionStart?: number, selectionEnd?: number) => {
    clearScheduledFocus();
    focusTimerRef.current = window.setTimeout(() => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      try {
        textarea.focus({ preventScroll: isMobilePanel });
      } catch {
        textarea.focus();
      }

      if (selectionStart !== undefined) {
        textarea.setSelectionRange(selectionStart, selectionEnd ?? selectionStart);
      }
      focusTimerRef.current = null;
    }, isMobilePanel ? 180 : 120);
  }, [clearScheduledFocus, isMobilePanel]);

  useEffect(() => () => {
    clearScheduledFocus();
    if (selectedCommentTimerRef.current !== null) {
      window.clearTimeout(selectedCommentTimerRef.current);
    }
  }, [clearScheduledFocus]);

  // Debug: Log pinned content when comments change
  useEffect(() => {
    comments.forEach(comment => {
      if (comment.type === 'pinned_message') {
        console.log(`[COMMENTS-PINNED] Comment ${comment.id} pinned content:`, {
          hasOriginalMessage: !!comment.pinnedContent?.originalMessage,
          originalMessageLength: comment.pinnedContent?.originalMessage?.length || 0,
          originalMessagePreview: comment.pinnedContent?.originalMessage?.substring(0, 200) || 'No content'
        });
      }
    });
  }, [comments]);

  // Handle pending timestamp (from clicking blue plus button)
  useEffect(() => {
    if (pendingTimestamp !== null && pendingTimestamp !== undefined) {
      // Format the timestamp and add it to the comment
      const formattedTime = formatSecondsToTime(pendingTimestamp);
      setNewComment(`${formattedTime} `);
      const len = `${formattedTime} `.length;
      focusComposer(len, len);
    }
  }, [focusComposer, pendingTimestamp]);

  // Real-time playhead time update for empty/timestamp-only comment box
  // This ensures there's always a timestamp ready when users start typing
  useEffect(() => {
    if (isMobilePanel || isComposerFocusedRef.current) {
      return;
    }

    // Check if the comment is empty or only contains a timestamp (like "1:23 " or "1:23:45 ")
    const timestampOnlyPattern = /^(\d{1,2}:\d{2}(:\d{2})?\s?)$/;
    const isEmptyOrTimestampOnly = newComment === '' || timestampOnlyPattern.test(newComment);

    if (isEmptyOrTimestampOnly) {
      const formattedTime = formatSecondsToTime(playheadTime);
      const newTimestamp = `${formattedTime} `;
      // Only update if the timestamp actually changed (avoids unnecessary re-renders)
      if (newComment !== newTimestamp) {
        setNewComment(newTimestamp);
      }
    }
  }, [isMobilePanel, playheadTime, newComment]);

  // Handle selected comment - scroll to it
  useEffect(() => {
    if (selectedCommentId && comments.length > 0) {
      // Find and scroll to the selected comment after a short delay
      if (selectedCommentTimerRef.current !== null) {
        window.clearTimeout(selectedCommentTimerRef.current);
      }
      selectedCommentTimerRef.current = window.setTimeout(() => {
        const element = document.getElementById(`comment-${selectedCommentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        selectedCommentTimerRef.current = null;
      }, 100);
    }
  }, [selectedCommentId, comments.length]); // Only depend on length, not the array reference

  // Organize comments into threads and sort by timeline position
  const threadedComments = useMemo(() => {
    const commentMap = new Map<string, Comment>();
    const rootComments: Comment[] = [];
    
    // First pass: create a map of all comments
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });
    
    // Second pass: organize into threads
    comments.forEach(comment => {
      const mappedComment = commentMap.get(comment.id);
      if (!mappedComment) return;
      
      if (comment.parentCommentId && commentMap.has(comment.parentCommentId)) {
        const parent = commentMap.get(comment.parentCommentId);
        if (parent) {
          parent.replies = parent.replies || [];
          parent.replies.push(mappedComment);
        }
      } else {
        rootComments.push(mappedComment);
      }
    });
    
    // Sort: timeline comments first (by timestamp), then non-timeline comments (by creation date)
    rootComments.sort((a, b) => {
      const aHasTimestamp = a.timestampSeconds !== null && a.timestampSeconds !== undefined;
      const bHasTimestamp = b.timestampSeconds !== null && b.timestampSeconds !== undefined;
      
      if (aHasTimestamp && bHasTimestamp) {
        // Both have timestamps - sort by timestamp
        return a.timestampSeconds! - b.timestampSeconds!;
      } else if (aHasTimestamp && !bHasTimestamp) {
        // a has timestamp, b doesn't - a comes first
        return -1;
      } else if (!aHasTimestamp && bHasTimestamp) {
        // b has timestamp, a doesn't - b comes first
        return 1;
      } else {
        // Neither has timestamp - sort by creation date
        const aDate = a.created_at || a.timestamp;
        const bDate = b.created_at || b.timestamp;
        if (!aDate || !bDate) return 0;
        return new Date(aDate).getTime() - new Date(bDate).getTime();
      }
    });
    
    // Also sort replies by timestamp within each thread
    rootComments.forEach(comment => {
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.sort((a, b) => {
          const aDate = a.created_at || a.timestamp;
          const bDate = b.created_at || b.timestamp;
          if (!aDate || !bDate) return 0;
          return new Date(aDate).getTime() - new Date(bDate).getTime();
        });
      }
    });
    
    return rootComments;
  }, [comments]);

  // Extract TTML blocks from message content (similar to ChatInterface)
  const extractTTMLBlocks = useCallback((content: string, commentId: string): TTMLFile[] => {
    console.log(`[COMMENTS] extractTTMLBlocks called for comment ${commentId}:`, {
      contentLength: content.length,
      contentPreview: content.substring(0, 200)
    });
    
    const blocks: TTMLFile[] = [];

    // Method 1: Look for traditional ```ttml blocks
    const ttmlRegex = /```ttml\n([\s\S]*?)\n```/g;
    let match;

    while ((match = ttmlRegex.exec(content)) !== null) {
      console.log(`[COMMENTS] Found traditional TTML block in comment ${commentId}:`, match[1].substring(0, 100));
      const ttmlContent = match[1].trim();
      if (ttmlContent) {
        const getTrackType = (content: string): string => {
          if (content.includes('r_overview') || content.includes('d_overview')) return 'overview';
          if (content.includes('r_clips') || content.includes('d_clips')) return 'clips';
          if (content.includes('r_best') || content.includes('d_best')) return 'best-parts';
          if (content.includes('r_weak') || content.includes('d_weak')) return 'weak-parts';
          if (content.includes('r_summary') || content.includes('d_summary')) return 'summary';
          if (content.includes('r_clip') || content.includes('d_clip')) return 'clip';
          if (content.includes('r_edit') || content.includes('d_edit')) return 'edit';
          return 'ttml';
        };

        const trackType = getTrackType(ttmlContent);
        const block = {
          id: `${trackType}-${commentId}-${blocks.length}`,
          content: ttmlContent,
          commentId,
          createdAt: new Date()
        };
        blocks.push(block);
      }
    }

    // Method 2: Look for TTML XML content directly (regardless of filename formatting)
    // Find all <tt>...</tt> blocks in the content
    const ttmlXmlRegex = /<tt[^>]*>[\s\S]*?<\/tt>/g;
    let xmlMatch;
    
    console.log(`[COMMENTS] Looking for <tt>...</tt> XML blocks in content...`);
    console.log(`[COMMENTS] Content includes "<tt"? ${content.includes('<tt')}`);

    while ((xmlMatch = ttmlXmlRegex.exec(content)) !== null) {
      const ttmlContent = xmlMatch[0].trim();
      console.log(`[COMMENTS] Found TTML XML block in comment ${commentId}:`, ttmlContent.substring(0, 100));
      
      // Generate a descriptive filename based on content or use generic name
      const generateTtmlFilename = (content: string, index: number): string => {
        // Try to extract any description from the content
        if (content.includes('r_overview') || content.includes('d_overview')) return 'overview.ttml';
        if (content.includes('r_clips') || content.includes('d_clips')) return 'clips.ttml';
        if (content.includes('r_best') || content.includes('d_best')) return 'best-parts.ttml';
        if (content.includes('r_weak') || content.includes('d_weak')) return 'weak-parts.ttml';
        if (content.includes('r_summary') || content.includes('d_summary')) return 'summary.ttml';
        if (content.includes('script_clips')) return `script-clips-${index + 1}.ttml`;
        if (content.includes('doom_debate')) return 'doom-debate.ttml';
        if (content.includes('main_interview')) return 'main-interview.ttml';
        if (content.includes('patreon_warmup')) return 'patreon-warmup.ttml';
        return `ttml-file-${index + 1}.ttml`;
      };

      const filename = generateTtmlFilename(ttmlContent, blocks.length);
      const baseId = filename.replace('.ttml', '');
      
      const block = {
        id: `${baseId}-${commentId}-${blocks.length}`,
        content: ttmlContent,
        commentId,
        createdAt: new Date(),
        filename: filename
      };
      blocks.push(block);
      console.log(`[COMMENTS] Created TTML block with filename: ${filename}`);
    }

    console.log(`[COMMENTS] Total TTML blocks found for comment ${commentId}: ${blocks.length}`);
    return blocks;
  }, []);

  // Download TTML file
  const downloadTTML = useCallback((ttmlFile: TTMLFile) => {
    const blob = new Blob([ttmlFile.content], { type: 'application/ttml+xml' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    
    // Use provided filename or generate one from ID
    let filename = ttmlFile.filename || `${ttmlFile.id}.ttml`;
    link.download = filename.endsWith('.ttml') ? filename : `${filename}.ttml`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }, []);

  // Remove TTML blocks from display content
  const removeCodeBlocks = useCallback((content: string): string => {
    return content.replace(/```ttml\n[\s\S]*?\n```/g, '').trim();
  }, []);

  // Copy comment to clipboard
  const copyCommentToClipboard = useCallback(async (content: string, commentId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedCommentId(commentId);
      setTimeout(() => setCopiedCommentId(null), 2000);
    } catch (err) {
      console.error('Failed to copy comment:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedCommentId(commentId);
        setTimeout(() => setCopiedCommentId(null), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  }, []);

  // Component to render TTML download buttons
  const TTMLDownloadButtons: React.FC<{ content: string; commentId: string }> = ({ content, commentId }) => {
    const ttmlFiles = extractTTMLBlocks(content, commentId);
    
    console.log(`[COMMENTS] TTMLDownloadButtons for comment ${commentId}:`, {
      content: content.substring(0, 100) + '...',
      ttmlFilesFound: ttmlFiles.length,
      ttmlFiles
    });
    
    if (ttmlFiles.length === 0) return null;

    return (
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
        <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">TTML Files Found:</div>
        <div className="flex flex-wrap gap-2">
          {ttmlFiles.map((ttmlFile) => (
            <button
              key={ttmlFile.id}
              onClick={() => downloadTTML(ttmlFile)}
              className={`flex items-center space-x-1 text-xs ${
                isDarkMode 
                  ? 'border border-[#445166] bg-[#232E3F] text-[#F4F4F4] hover:bg-[#2F3B4E]' 
                  : 'border border-[#E7B14C]/45 bg-[#FFF4D8] text-[#8B5B00] hover:bg-[#FFE7AD]'
              } px-2 py-1 rounded transition-colors`}
            >
              <FileText size={12} />
              <span>Download {ttmlFile.filename ? ttmlFile.filename.replace('.ttml', '').toUpperCase() : ttmlFile.id.split('-')[0].toUpperCase()}</span>
              <Download size={10} />
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Track user activity for idle detection with exponential backoff reset
  useEffect(() => {
    const resetIdleTimer = () => {
      // Reset exponential backoff on user activity
      currentPollIntervalRef.current = BASE_POLL_INTERVAL;

      // Only update idle state if it's actually changing
      setIsIdle(prevIdle => {
        if (prevIdle === false) return false; // Already active, no change needed
        return false;
      });

      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      idleTimeoutRef.current = setTimeout(() => {
        setIsIdle(true);
      }, 60000); // 1 minute of inactivity = idle
    };

    // Remove 'mousemove' from events to reduce re-renders during playback
    // Keep only significant interaction events
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer, true);
    });

    resetIdleTimer(); // Initialize timer

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetIdleTimer, true);
      });
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, []);

  const loadComments = useCallback(async (silent = false) => {
    if (!sessionId) return;
    if (!isPublicView && !token) return;

    if (!silent) {
      setIsLoading(true);
    }
    setError('');

    try {
      // Public views must use share token; authenticated users use session API
      if (isPublicView && !token) {
        console.error('[COMMENTS] Public view requires a share token');
        setError('Comments unavailable - share token required');
        return;
      }
      const url = isPublicView
        ? `${API_BASE_URL}/api/public/share/${token}/comments`
        : `${API_BASE_URL}/api/sessions/${sessionId}/comments`;
      console.log('[COMMENTS] Making request to:', url);
      console.log('[COMMENTS] With token:', token ? 'present' : 'missing');
      console.log('[COMMENTS] Public view:', isPublicView);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (!isPublicView && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(url, { headers });

      console.log('[COMMENTS] Response status:', response.status);
      console.log('[COMMENTS] Response headers:', Object.fromEntries(response.headers.entries()));
      
      const responseText = await response.text();
      console.log('[COMMENTS] Raw response:', responseText.substring(0, 200) + '...');

      if (!response.ok) {
        throw new Error('Failed to load comments');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[COMMENTS] JSON parse error:', parseError);
        throw new Error('Server returned invalid JSON');
      }
      const loadedComments = data.comments || [];
      console.log('[COMMENTS] Loaded comments:', loadedComments);
      console.log('[COMMENTS] First comment structure:', loadedComments[0]);
      // Debug: Check the exact structure of comments
      if (loadedComments.length > 0) {
        console.log('[COMMENTS] Sample comment keys:', Object.keys(loadedComments[0]));
        console.log('[COMMENTS] Comments with timestampSeconds:', loadedComments.filter((c: any) => c.timestampSeconds !== null && c.timestampSeconds !== undefined).length);
        console.log('[COMMENTS] Comments with timestamp_seconds:', loadedComments.filter((c: any) => c.timestamp_seconds !== null && c.timestamp_seconds !== undefined).length);
      }
      setComments(loadedComments);
      if (onCommentsChange) {
        console.log('[COMMENTS] Calling onCommentsChange with', loadedComments.length, 'comments');
        onCommentsChange(loadedComments);
      } else {
        console.log('[COMMENTS] No onCommentsChange callback provided');
      }
    } catch (err) {
      console.error('Failed to load comments:', err);
      if (!silent) {
        setError('Failed to load comments');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [sessionId, token, isPublicView, onCommentsChange]);
  
  // Create stable reference to loadComments
  const loadCommentsRef = useRef(loadComments);
  useEffect(() => {
    loadCommentsRef.current = loadComments;
  }, [loadComments]);

  // Load reactions for comments
  const loadReactions = useCallback(async () => {
    // Skip reactions for public views for now
    if (!sessionId || !token || isPublicView) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/reactions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReactions(data.reactions || {});
      }
    } catch (error) {
      console.error('Failed to load reactions:', error);
    }
  }, [sessionId, token, isPublicView]);
  
  // Create stable reference to loadReactions
  const loadReactionsRef = useRef(loadReactions);
  useEffect(() => {
    loadReactionsRef.current = loadReactions;
  }, [loadReactions]);

  // Initial load when component mounts
  useEffect(() => {
    if (!sessionId || (!isPublicView && !token)) {
      return;
    }

    // Skip initial load if we have pre-loaded comments from CDN bundle
    if (disablePolling && initialComments && initialComments.length > 0) {
      console.log('[COMMENTS] Using pre-loaded comments from CDN bundle, skipping API fetch');
      return;
    }

    loadCommentsRef.current();
    loadReactionsRef.current();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, token, disablePolling]);

  // Set up auto-refresh with exponential backoff to reduce server load
  useEffect(() => {
    // Skip polling entirely for CDN-served public shares
    if (disablePolling) {
      console.log('[COMMENTS] Polling disabled for static/CDN content');
      return;
    }

    if (!sessionId || (!isPublicView && !token)) {
      return;
    }

    const scheduleNextPoll = () => {
      // Clear any existing timeout
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }

      pollTimeoutRef.current = setTimeout(() => {
        // Only refresh if idle
        if (isIdleRef.current) {
          loadCommentsRef.current(true); // Silent refresh
          loadReactionsRef.current();

          // Exponential backoff: double the interval up to max
          currentPollIntervalRef.current = Math.min(
            currentPollIntervalRef.current * 2,
            MAX_POLL_INTERVAL
          );
          console.log(`[COMMENTS] Next poll in ${currentPollIntervalRef.current / 1000}s (exponential backoff)`);
        }
        // Schedule next poll regardless
        scheduleNextPoll();
      }, currentPollIntervalRef.current);
    };

    // Start the polling cycle
    scheduleNextPoll();

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [sessionId, token, isPublicView, disablePolling]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const isNearBottom = () => {
    if (!messagesContainerRef.current) return true;
    const container = messagesContainerRef.current;
    const threshold = 100; // pixels from bottom
    return container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
  };

  // Smart auto-scroll: only scroll when new comments are added and user is near bottom
  useEffect(() => {
    const hasNewComments = comments.length > previousCommentsLength.current;
    const shouldAutoScroll = !isInline && hasNewComments && isNearBottom();
    
    if (shouldAutoScroll) {
      scrollToBottom();
    }
    
    previousCommentsLength.current = comments.length;
  }, [comments, isInline]);

  // Initial scroll when opening
  useEffect(() => {
    if (!isInline && comments.length > 0) {
      scrollToBottom();
    }
  }, [isInline]);

  const handleTimestampClick = useCallback((seconds: number) => {
    if (seekToRef.current) {
      seekToRef.current(seconds);
    }
  }, []);

  const addComment = async () => {
    console.log('[COMMENTS] addComment called', {
      newComment: newComment.trim(),
      sessionId,
      isPublicView,
      token: token ? 'present' : 'missing',
      user: user ? 'present' : 'missing',
      sharePermissions
    });

    if (!newComment.trim() || !sessionId) {
      console.log('[COMMENTS] addComment early return - no comment or sessionId', {
        hasComment: !!newComment.trim(),
        hasSessionId: !!sessionId
      });
      return;
    }
    
    if (!isPublicView && (!token || !user)) {
      console.log('[COMMENTS] addComment early return - not public view and missing token/user', {
        isPublicView,
        hasToken: !!token,
        hasUser: !!user
      });
      return;
    }
    
    // Check share permissions if viewing through share link
    if (sharePermissions && !sharePermissions.commentsWrite) {
      console.log('[COMMENTS] addComment blocked by share permissions', sharePermissions);
      setError('Comments are read-only for this share link');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Extract timestamp from comment if present
      const firstTimestamp = parseTimestamp(newComment.trim());
      const timestampSeconds = firstTimestamp?.seconds;

      // Public views must use share token; authenticated users use session API
      if (isPublicView && !token) {
        console.error('[COMMENTS] Public view requires a share token');
        setError('Cannot post comment - share token required');
        return;
      }
      const url = isPublicView
        ? `${API_BASE_URL}/api/public/share/${token}/comments`
        : `${API_BASE_URL}/api/sessions/${sessionId}/comments`;

      console.log('[COMMENTS] Posting comment to:', url);
        
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (!isPublicView && token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (isPublicView) {
        // Add anonymous session ID for public comments
        const anonymousSessionId = getAnonymousSessionId();
        if (anonymousSessionId) {
          headers['X-Anonymous-Session-ID'] = anonymousSessionId;
        }
      }
      
      const body = isPublicView 
        ? {
            content: newComment.trim(),
            type: 'comment',
            anonName: anonName.trim(),
            timestampSeconds,
            parentCommentId: replyingTo
          }
        : {
            content: newComment.trim(),
            type: 'comment',
            timestampSeconds,
            parentCommentId: replyingTo
          };
      
      console.log('[COMMENTS] Request body:', body);
      console.log('[COMMENTS] Request headers:', headers);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      console.log('[COMMENTS] Response status:', response.status);
      const responseText = await response.text();
      console.log('[COMMENTS] Response text:', responseText);

      if (!response.ok) {
        throw new Error(`Failed to add comment: ${response.status} - ${responseText}`);
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[COMMENTS] Failed to parse response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response');
      }

      console.log('[COMMENTS] Comment added successfully:', data);
      
      setNewComment('');
      setReplyingTo(null);
      
      // Reload all comments to get proper structure with replies
      console.log('[COMMENTS] Reloading comments after add...');
      await loadCommentsRef.current();
      console.log('[COMMENTS] Comments reloaded after add');
      
      // Force immediate update of parent component
      // This ensures the timeline gets updated even if React batches the state updates
      if (onCommentsChange && comments) {
        // Use setTimeout to ensure this runs after the state update from loadComments
        setTimeout(() => {
          const currentComments = comments; // Get the current state
          console.log('[COMMENTS] Force updating parent with current comments');
          loadCommentsRef.current(); // This will trigger onCommentsChange with fresh data
        }, 100);
      }
    } catch (err) {
      console.error('[COMMENTS] Failed to add comment:', err);
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteComment = async (commentId: string) => {
    if (!sessionId) return;

    setError('');

    try {
      let response;

      if (isPublicView) {
        // Public deletion requires share token
        if (!token) {
          setError('Cannot delete comment - share token required');
          return;
        }
        const anonymousSessionId = getAnonymousSessionId();
        response = await fetch(`${API_BASE_URL}/api/public/share/${token}/comments/${commentId}`, {
          method: 'DELETE',
          headers: {
            'X-Anonymous-Session-ID': anonymousSessionId || '',
          },
        });
      } else if (token && user) {
        // Authenticated deletion
        response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments/${commentId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } else {
        throw new Error('Not authorized to delete comment');
      }

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      const updatedComments = comments.filter(comment => comment.id !== commentId);
      setComments(updatedComments);
      if (onCommentsChange) {
        onCommentsChange(updatedComments);
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
      setError('Failed to delete comment');
    }
  };

  const canDeleteComment = (comment: Comment) => {
    // For authenticated users
    if (!isPublicView && user) {
      // Users can delete their own comments or tim@mlst.ai can delete any
      const canDelete = comment.userEmail === user.email || user.email === 'tim@mlst.ai';
      console.log('[DELETE] Checking delete permission for authenticated user:', {
        commentEmail: comment.userEmail,
        userEmail: user.email,
        canDelete
      });
      return canDelete;
    }
    
    // For public share users with write permissions
    if (isPublicView && sharePermissions?.commentsWrite && comment.isAnonymous) {
      // Check if this comment belongs to the current anonymous session
      const anonymousSessionId = getAnonymousSessionId();
      const canDelete = comment.anonymousSessionId === anonymousSessionId;
      console.log('[DELETE] Checking delete permission for anonymous user:', {
        commentSessionId: comment.anonymousSessionId,
        currentSessionId: anonymousSessionId,
        canDelete
      });
      return canDelete;
    }
    
    return false;
  };

  // Start replying to a comment
  const startReply = (comment: Comment) => {
    setReplyingTo(comment.id);
    setReplyingToComment(comment);
    setNewComment('');

    focusComposer();
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyingTo(null);
    setReplyingToComment(null);
    setNewComment('');
  };

  // Toggle thread expansion
  const toggleThread = (commentId: string) => {
    setExpandedThreads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  // Handle emoji reaction
  const handleReaction = useCallback(async (commentId: string, emoji: string) => {
    if (!sessionId || !token || !user) return;
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/sessions/${sessionId}/comments/${commentId}/reactions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ emoji }),
        }
      );

      if (response.ok) {
        // Reload reactions
        await loadReactions();
      }
    } catch (error) {
      console.error('Failed to handle reaction:', error);
    }
  }, [sessionId, token, user, loadReactions]);


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addComment();
    }
  };

  const formatTimestamp = (timestamp: Date | string | undefined | null) => {
    if (!timestamp) {
      return 'Unknown time';
    }
    
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const getUserInitials = (nameOrEmail: string) => {
    if (!nameOrEmail) {
      return 'GU'; // Guest User
    }
    // If it's an email, extract the part before @
    if (nameOrEmail.includes('@')) {
      return nameOrEmail.split('@')[0].substring(0, 2).toUpperCase();
    }
    // If it's a name, use first two letters or initials
    const words = nameOrEmail.trim().split(' ');
    if (words.length > 1) {
      // Use first letter of first and last name
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    // Single word name - use first two letters
    return nameOrEmail.substring(0, 2).toUpperCase();
  };

  const getUserColor = (email: string) => {
    const colors = [
      'bg-[#232E3F]', 'bg-[#435065]', 'bg-[#8B5B00]', 'bg-[#B56A5D]',
      'bg-[#FFB52B]', 'bg-[#5C6675]', 'bg-[#6B5130]', 'bg-[#A48757]'
    ];
    if (!email) {
      return colors[0]; // Default to blue if email is undefined
    }
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Formatting helper functions
  const insertFormatting = (before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = newComment.substring(start, end);
    
    const beforeText = newComment.substring(0, start);
    const afterText = newComment.substring(end);
    
    const newText = `${beforeText}${before}${selectedText}${after}${afterText}`;
    setNewComment(newText);

    const newCursorPos = start + before.length + selectedText.length;
    focusComposer(newCursorPos, newCursorPos);
  };

  const formatBold = () => insertFormatting('**', '**');
  const formatItalic = () => insertFormatting('*', '*');
  const formatQuote = () => {
    const start = textareaRef.current?.selectionStart || 0;
    const beforeCursor = newComment.substring(0, start);
    const lineStart = beforeCursor.lastIndexOf('\n') + 1;
    
    // Add quote to current line
    const beforeLine = newComment.substring(0, lineStart);
    const currentLine = newComment.substring(lineStart);
    
    setNewComment(`${beforeLine}> ${currentLine}`);
  };

  // Generate TTML from comments
  const generateTTMLFromComments = useCallback(() => {
    // Separate comments with timestamps from those without
    const commentsWithTimestamp = comments.filter(c =>
      !c.parentCommentId && c.timestampSeconds !== undefined && c.timestampSeconds !== null
    );
    const commentsWithoutTimestamp = comments.filter(c =>
      !c.parentCommentId && (c.timestampSeconds === undefined || c.timestampSeconds === null)
    );

    // Sort timestamped comments by timestamp
    const sortedTimestampedComments = [...commentsWithTimestamp].sort((a, b) => {
      return a.timestampSeconds! - b.timestampSeconds!;
    });

    // Sort timeless comments by creation date
    const sortedTimelessComments = [...commentsWithoutTimestamp].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.created_at || a.timestamp || 0);
      const dateB = new Date(b.createdAt || b.created_at || b.timestamp || 0);
      return dateA.getTime() - dateB.getTime();
    });

    // Build sorted comments list with timestamped comments first, but we'll handle
    // timeless comments separately at the very beginning
    const sortedComments = [...sortedTimestampedComments];

    // Line wrap function at 42 characters
    const wrapText = (text: string, maxLength: number = 42): string => {
      const words = text.split(' ');
      const lines: string[] = [];
      let currentLine = '';

      words.forEach(word => {
        if ((currentLine + ' ' + word).trim().length <= maxLength) {
          currentLine = currentLine ? currentLine + ' ' + word : word;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      });
      if (currentLine) lines.push(currentLine);
      
      return lines.join('<br/>');
    };

    // Convert markdown to HTML - simple implementation
    const markdownToHTML = (markdown: string): string => {
      let html = markdown;
      
      // First escape XML special characters
      html = html.replace(/&/g, '&amp;');
      html = html.replace(/</g, '&lt;');
      html = html.replace(/>/g, '&gt;');
      html = html.replace(/"/g, '&quot;');
      html = html.replace(/'/g, '&apos;');
      
      // Convert bold (non-greedy)
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      
      // Convert italic (but not if it's part of bold)
      html = html.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
      
      // Convert code
      html = html.replace(/`(.+?)`/g, '<code>$1</code>');
      
      // Convert line breaks
      html = html.replace(/\n/g, '<br/>');
      
      return html;
    };

    // Generate TTML content
    let ttmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<tt xml:lang="en" xmlns="http://www.w3.org/ns/ttml" xmlns:ttm="http://www.w3.org/ns/ttml#metadata" xmlns:tts="http://www.w3.org/ns/ttml#styling" xmlns:ttp="http://www.w3.org/ns/ttml#parameter" xmlns:ittp="http://www.w3.org/ns/ttml/profile/imsc1#parameter" xmlns:itts="http://www.w3.org/ns/ttml/profile/imsc1#styling" ttp:profile="http://www.w3.org/ns/ttml/profile/imsc1/text" ttp:frameRate="24" ttp:frameRateMultiplier="1000 1001" ttp:dropMode="nonDrop" ttp:timeBase="media">
  <head>
    <styling>
      <style xml:id="r_comments_style" tts:color="#ffffff" tts:opacity="1" tts:fontSize="75%" tts:fontFamily="proportionalSansSerif" tts:fontWeight="normal" tts:textOutline="#000000 2px" tts:textAlign="start"/>
    </styling>
    <layout>
      <region xml:id="r_comments" tts:origin="2.970% 49.977%" tts:extent="94.060% 46.667%" tts:displayAlign="before" tts:backgroundColor="#000000" tts:opacity="0.50" tts:showBackground="whenActive"/>
    </layout>
  </head>
  <body>
    <div xml:id="d_comments" region="r_comments" style="r_comments_style">`;

    // Format timestamps helper
    const formatTime = (seconds: number): string => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      const millis = Math.floor((seconds % 1) * 1000);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`;
    };

    // First, add all timeless comments as a single concatenated entry at the very beginning
    if (sortedTimelessComments.length > 0) {
      const timelessParts: string[] = [];

      sortedTimelessComments.forEach(comment => {
        let content = comment.content;
        content = markdownToHTML(content);
        content = wrapText(content);
        const author = comment.displayName || comment.userName || comment.userEmail;
        timelessParts.push(`[${author}] ${content}`);

        // Include replies for this timeless comment
        if (comment.replies && comment.replies.length > 0) {
          comment.replies.forEach(reply => {
            let replyContent = markdownToHTML(reply.content);
            replyContent = wrapText(replyContent);
            const replyAuthor = reply.displayName || reply.userName || reply.userEmail;
            timelessParts.push(`  ↳ [${replyAuthor}] ${replyContent}`);
          });
        }
      });

      // Concatenate all timeless comments with line breaks
      const concatenatedTimeless = timelessParts.join('<br/><br/>');

      // Calculate end time: 30 seconds or until first timestamped comment
      let endSeconds = 30;
      if (sortedComments.length > 0 && sortedComments[0].timestampSeconds !== undefined) {
        endSeconds = Math.min(30, sortedComments[0].timestampSeconds);
      }

      ttmlContent += `
      <p begin="00:00:00.000" end="${formatTime(endSeconds)}">[General Notes]<br/>${concatenatedTimeless}</p>`;
    }

    // Now process timestamped comments
    sortedComments.forEach((comment, index) => {
      // Skip replies as they're included with parent comments
      if (comment.parentCommentId) return;

      // Calculate start time (all comments here have timestamps)
      const startSeconds = comment.timestampSeconds!;

      // Calculate end time (20 seconds duration or until next comment)
      let endSeconds = startSeconds + 20;

      // Check if there's a next comment with timestamp that would overlap
      const nextComment = sortedComments.find((c, i) =>
        i > index &&
        !c.parentCommentId &&
        c.timestampSeconds !== undefined &&
        c.timestampSeconds > startSeconds
      );

      if (nextComment && nextComment.timestampSeconds! < endSeconds) {
        endSeconds = nextComment.timestampSeconds!;
      }

      const startTime = formatTime(startSeconds);
      const endTime = formatTime(endSeconds);

      // Process comment content
      let content = comment.content;

      // Convert markdown to HTML
      content = markdownToHTML(content);

      // Apply line wrapping
      content = wrapText(content);

      // Add author info
      const author = comment.displayName || comment.userName || comment.userEmail;
      const formattedContent = `[${author}] ${content}`;

      // Add comment to TTML
      ttmlContent += `
      <p begin="${startTime}" end="${endTime}">${formattedContent}</p>`;

      // Add replies if any
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.forEach(reply => {
          let replyContent = markdownToHTML(reply.content);
          replyContent = wrapText(replyContent);
          const replyAuthor = reply.displayName || reply.userName || reply.userEmail;
          const formattedReply = `  ↳ [${replyAuthor}] ${replyContent}`;
          ttmlContent += `
      <p begin="${startTime}" end="${endTime}">${formattedReply}</p>`;
        });
      }
    });

    ttmlContent += `
    </div>
  </body>
</tt>`;

    return ttmlContent;
  }, [comments]);

  // Download TTML file
  const downloadCommentsTTML = useCallback(() => {
    const ttmlContent = generateTTMLFromComments();
    const blob = new Blob([ttmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.download = `comments_${sessionId}_${timestamp}.ttml`;
    link.click();
    URL.revokeObjectURL(url);
  }, [generateTTMLFromComments, sessionId]);

  // Component to render comment text with clickable timestamps
  const CommentTextWithTimestamps: React.FC<{ content: string }> = ({ content }) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const timestampRegex = /\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/g;
    let match;
    let keyIndex = 0;
    
    while ((match = timestampRegex.exec(content)) !== null) {
      // Add text before the timestamp
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${keyIndex++}`}>
            {content.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      const timestamp = parseTimestamp(match[0]);
      if (timestamp) {
        // Add the clickable timestamp
        parts.push(
          <button
            key={`timestamp-${keyIndex++}`}
            onClick={() => handleTimestampClick(timestamp.seconds)}
            className="mx-1 font-mono text-sm text-[#B57700] hover:text-[#8B5B00] underline"
            title={`Jump to ${timestamp.formattedTime}`}
          >
            {timestamp.originalText}
          </button>
        );
      } else {
        // If parsing failed, just add the text
        parts.push(
          <span key={`text-${keyIndex++}`}>
            {match[0]}
          </span>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add any remaining text
    if (lastIndex < content.length) {
      parts.push(
        <span key={`text-${keyIndex++}`}>
          {content.substring(lastIndex)}
        </span>
      );
    }
    
    return <>{parts.length > 0 ? parts : content}</>;
  };

  const toggleFullscreen = () => {
    if (isInline || isMobilePanel) {
      return;
    }
    setIsFullscreen(!isFullscreen);
  };

  // Handle drag resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartHeight(panelHeight);
  };

  const handlePanelDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingPanel(true);
    setDragStartPos({ 
      x: e.clientX - panelPosition.x, 
      y: e.clientY - panelPosition.y 
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const deltaY = dragStartY - e.clientY;
      const newHeight = Math.max(200, Math.min(window.innerHeight - 100, dragStartHeight + deltaY));
      setPanelHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, dragStartY, dragStartHeight]);

  useEffect(() => {
    const handlePanelMouseMove = (e: MouseEvent) => {
      if (!isDraggingPanel) return;
      
      const newX = e.clientX - dragStartPos.x;
      const newY = e.clientY - dragStartPos.y;
      
      // Keep panel within screen bounds
      const maxX = window.innerWidth - 480;
      const maxY = window.innerHeight - panelHeight;
      
      setPanelPosition({
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY))
      });
    };

    const handlePanelMouseUp = () => {
      setIsDraggingPanel(false);
    };

    if (isDraggingPanel) {
      document.addEventListener('mousemove', handlePanelMouseMove);
      document.addEventListener('mouseup', handlePanelMouseUp);
      document.body.style.cursor = 'move';
      
      return () => {
        document.removeEventListener('mousemove', handlePanelMouseMove);
        document.removeEventListener('mouseup', handlePanelMouseUp);
        document.body.style.cursor = '';
      };
    }
  }, [isDraggingPanel, dragStartPos, panelHeight]);

  // Recursive component to render comment threads
  const CommentThread: React.FC<{ comment: Comment; depth: number }> = ({ comment, depth }) => {
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isExpanded = expandedThreads.has(comment.id);
    
    return (
      <div
        className={`${depth > 0 ? 'ml-8 mt-2' : ''}`}
        id={`comment-${comment.id}`}
      >
        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-3 ${
          selectedCommentId === comment.id ? 'ring-2 ring-[#FFB52B]' : ''
        }`}>
          <div className="flex items-start space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white ${comment.avatarColor ? '' : getUserColor(comment.displayName || comment.userName || comment.userEmail)}`}
              style={comment.avatarColor ? { backgroundColor: comment.avatarColor } : {}}>
              {getUserInitials(comment.displayName || comment.userName || comment.userEmail)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {comment.displayName || comment.userName || comment.userEmail}
                  </span>
                  {comment.timestampSeconds !== null && comment.timestampSeconds !== undefined && (
                    <button
                      onClick={() => handleTimestampClick(comment.timestampSeconds!)}
                      className="font-mono text-xs text-[#B57700] hover:text-[#8B5B00]"
                    >
                      {formatSecondsToTime(comment.timestampSeconds)}
                    </button>
                  )}
                  <span className="text-xs text-gray-500 flex items-center">
                    <Clock size={12} className="mr-1" />
                    {formatTimestamp(comment.createdAt || comment.created_at || comment.timestamp)}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  {/* Reply Button - show for authenticated users or public users with write permissions */}
                  {(!isPublicView || (isPublicView && sharePermissions?.commentsWrite)) && (
                    <button
                      onClick={() => startReply(comment)}
                      className={`p-1 rounded transition-colors ${
                        isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                      }`}
                      title="Reply"
                    >
                      <Reply size={14} />
                    </button>
                  )}
                  
                  {/* Copy Button */}
                  <button
                    onClick={() => copyCommentToClipboard(
                      comment.type === 'pinned_message' 
                        ? comment.pinnedContent?.originalMessage || comment.content
                        : comment.content, 
                      comment.id
                    )}
                    className={`p-1 rounded transition-colors ${
                      copiedCommentId === comment.id
                        ? `${isDarkMode ? 'text-[#FFCF74] bg-[#332A17]' : 'text-[#B57700] bg-[#FFF4D8]'}`
                        : `${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'}`
                    }`}
                    title="Copy comment to clipboard"
                  >
                    {copiedCommentId === comment.id ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  
                  {/* Delete Button */}
                  {canDeleteComment(comment) && (
                    <button
                      onClick={() => deleteComment(comment.id)}
                      className={`text-gray-400 hover:text-red-500 transition-colors p-1 rounded ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                      title="Delete comment"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Comment content */}
              {comment.type === 'pinned_message' ? (
                <div className={`${isDarkMode ? 'bg-yellow-900 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border rounded p-3`}>
                  <div className={`text-xs ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'} font-medium mb-2 flex items-center`}>
                    📌 Pinned from {comment.pinnedContent?.model}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} bg-white dark:bg-gray-700 rounded p-3 prose prose-sm max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => {
                          if (typeof children === 'string') {
                            return (
                              <div className={`mb-2 last:mb-0 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                <CommentTextWithTimestamps content={children} />
                              </div>
                            );
                          }
                          return <div className={`mb-2 last:mb-0 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</div>;
                        },
                        ul: ({ children }) => <ul className={`list-disc list-inside mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</ul>,
                        ol: ({ children }) => <ol className={`list-decimal list-inside mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</ol>,
                        li: ({ children }) => <li className={`mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</li>,
                        h1: ({ children }) => <h1 className={`text-base font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h1>,
                        h2: ({ children }) => <h2 className={`text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h2>,
                        h3: ({ children }) => <h3 className={`text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h3>,
                        strong: ({ children }) => <strong className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</strong>,
                        em: ({ children }) => <em className={`italic ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</em>,
                        blockquote: ({ children }) => <blockquote className={`border-l-4 ${isDarkMode ? 'border-gray-500 text-gray-300' : 'border-gray-300 text-gray-700'} pl-4 italic mb-2`}>{children}</blockquote>,
                        code: ({ className, children, ...props }: any) => {
                          const match = /language-(\w+)/.exec(className || '');
                          const isInline = !props.node || props.node.tagName !== 'pre';
                          const isCodeBlock = !isInline && match;
                          
                          if (isCodeBlock && match[1] === 'ttml') {
                            return null;
                          }
                          
                          const codeClasses = isInline 
                            ? `${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'} px-1 py-0.5 rounded text-xs`
                            : `block ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'} p-2 rounded text-xs overflow-x-auto`;
                          return (
                            <code className={`${className} ${codeClasses}`} {...props}>
                              {children}
                            </code>
                          );
                        }
                      }}
                    >
                      {removeCodeBlocks(comment.pinnedContent?.originalMessage || '')}
                    </ReactMarkdown>
                  </div>
                  <TTMLDownloadButtons 
                    content={comment.pinnedContent?.originalMessage || ''} 
                    commentId={comment.id} 
                  />
                </div>
              ) : (
                <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} prose prose-sm max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => {
                        if (typeof children === 'string') {
                          return (
                            <p className={`mb-2 last:mb-0 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              <CommentTextWithTimestamps content={children} />
                            </p>
                          );
                        }
                        return <p className={`mb-2 last:mb-0 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</p>;
                      },
                      ul: ({ children }) => <ul className={`list-disc list-inside mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</ul>,
                      ol: ({ children }) => <ol className={`list-decimal list-inside mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</ol>,
                      li: ({ children }) => <li className={`mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</li>,
                      h1: ({ children }) => <h1 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h1>,
                      h2: ({ children }) => <h2 className={`text-base font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h2>,
                      h3: ({ children }) => <h3 className={`text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h3>,
                      strong: ({ children }) => <strong className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</strong>,
                      em: ({ children }) => <em className={`italic ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</em>,
                      blockquote: ({ children }) => <blockquote className={`border-l-4 ${isDarkMode ? 'border-gray-500 text-gray-300' : 'border-gray-300 text-gray-700'} pl-4 italic mb-2`}>{children}</blockquote>,
                      code: ({ className, children, ...props }: any) => {
                        const isInline = !className;
                        const codeClasses = isInline 
                          ? `${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'} px-1 py-0.5 rounded text-xs`
                          : `block ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'} p-2 rounded text-xs overflow-x-auto`;
                        return (
                          <code className={codeClasses} {...props}>
                            {children}
                          </code>
                        );
                      },
                      a: ({ children, href }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#B57700] hover:text-[#8B5B00] underline">
                          {children}
                        </a>
                      )
                    }}
                  >
                    {comment.content}
                  </ReactMarkdown>
                </div>
              )}
              
              {/* Emoji reactions */}
              {!isPublicView && (
                <div className="flex items-center gap-2 mt-2">
                  {/* Show existing reactions */}
                  {reactions[comment.id]?.map((reaction, idx) => {
                    const userList = reaction.users?.split(',').map(email => {
                      const name = email.split('@')[0];
                      return name.charAt(0).toUpperCase() + name.slice(1);
                    }).join(', ');
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => handleReaction(comment.id, reaction.emoji)}
                        className={`relative group flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                          reaction.userReacted
                            ? isDarkMode 
                              ? 'border border-[#445166] bg-[#232E3F] text-[#F4F4F4] hover:bg-[#2F3B4E]'
                              : 'border border-[#E7B14C]/45 bg-[#FFF4D8] text-[#8B5B00] hover:bg-[#FFE7AD]'
                            : isDarkMode
                              ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        title={userList || 'No users'}
                      >
                        <span>{reaction.emoji}</span>
                        <span>{reaction.count}</span>
                        
                        {/* Tooltip */}
                        {userList && (
                          <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 ${
                            isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-800 text-white'
                          }`}>
                            {userList}
                            <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 ${
                              isDarkMode ? 'border-t-gray-900' : 'border-t-gray-800'
                            }`}></div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                  
                  {/* Add reaction buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleReaction(comment.id, '👍')}
                      className={`p-1.5 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200'
                          : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                      }`}
                      title="Thumbs up"
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      onClick={() => handleReaction(comment.id, '❤️')}
                      className={`p-1.5 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200'
                          : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                      }`}
                      title="Heart"
                    >
                      <Heart size={14} />
                    </button>
                    <button
                      onClick={() => handleReaction(comment.id, '😊')}
                      className={`p-1.5 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200'
                          : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                      }`}
                      title="Smile"
                    >
                      <Smile size={14} />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Thread toggle and replies count */}
              {hasReplies && (
                <button
                  onClick={() => toggleThread(comment.id)}
                  className={`flex items-center gap-1 mt-2 text-xs ${
                    isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span>{comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Render replies */}
        {hasReplies && isExpanded && (
          <div className="mt-2">
            {comment.replies!.map((reply) => (
              <CommentThread key={reply.id} comment={reply} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Don't render if no session
  if (!sessionId) return null;

  const effectiveFullscreen = !isInline && (isFullscreen || isMobilePanel);
  const floatingPanelStyle = isInline
    ? { height: '576px' }
    : effectiveFullscreen
      ? isMobilePanel
        ? {
            left: '0.75rem',
            right: '0.75rem',
            top: 'calc(var(--app-mobile-top-offset) + 0.75rem)',
            bottom: 'calc(var(--app-mobile-bottom-offset) + 0.75rem)',
          }
        : undefined
      : {
          height: `${panelHeight}px`,
          left: `${panelPosition.x}px`,
          top: `${panelPosition.y}px`,
        };

  const renderPanelHeader = (compactTouchLayout: boolean) => {
    const canDragPanel = !compactTouchLayout && !isInline && !effectiveFullscreen;
    const headerToneClass = compactTouchLayout
      ? isDarkMode
        ? 'border-gray-700 bg-[#16202c]'
        : 'border-gray-200 bg-white'
      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900';
    const actionButtonClass = compactTouchLayout
      ? isDarkMode
        ? 'rounded-full border border-white/10 bg-white/[0.04] p-2 text-gray-300'
        : 'rounded-full border border-gray-200 bg-[#f5f7fa] p-2 text-[#5b687c]'
      : 'rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700';

    return (
      <div
        className={`flex shrink-0 items-center justify-between border-b px-4 ${compactTouchLayout ? 'py-3' : 'py-4'} ${headerToneClass}`}
        onMouseDown={canDragPanel ? handlePanelDragStart : undefined}
        style={{ cursor: canDragPanel ? 'move' : 'default' }}
      >
        <div className="flex min-w-0 items-center space-x-3">
          <MessageCircle size={20} className="shrink-0 text-[#B57700] dark:text-[#FFCF74]" />
          {compactTouchLayout ? (
            <div className="min-w-0">
              <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                isDarkMode ? 'text-[#ffcf74]' : 'text-[#8a6115]'
              }`}>
                Comments
              </div>
              <div className={`truncate text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-[#232E3F]'}`}>
                {isPublicView ? 'Public view' : 'Session comments'}
              </div>
              <p className={`mt-0.5 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {comments.length} comment{comments.length !== 1 ? 's' : ''} • Markdown supported
                {isPublicView && ' • No account required'}
              </p>
            </div>
          ) : (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Comments {isFullscreen && '(Fullscreen)'} {isPublicView && '• Public View'}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {comments.length} comment{comments.length !== 1 ? 's' : ''} • Markdown supported
                {isPublicView && ' • No account required'}
              </p>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center space-x-2">
          {!sharePermissions?.isPublicShare && comments.length > 0 && (
            <button
              onClick={downloadCommentsTTML}
              className={actionButtonClass}
              title="Download comments as TTML"
            >
              <Download size={18} />
            </button>
          )}
          {!isInline && !compactTouchLayout && (
            <button
              onClick={toggleFullscreen}
              className={actionButtonClass}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          )}
          <button
            onClick={() => {
              onClose?.();
            }}
            className={actionButtonClass}
            title="Close comments"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    );
  };

  const renderCommentsList = (compactTouchLayout: boolean) => (
    <div
      ref={messagesContainerRef}
      className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4"
      data-allow-touch-scroll="true"
      style={compactTouchLayout ? { WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' } : undefined}
    >
      {isLoading && comments.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <MessageCircle size={48} className="mx-auto mb-4 text-gray-300" />
          <p>Loading comments...</p>
        </div>
      ) : comments.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <MessageCircle size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No comments yet</p>
          <p className="text-sm">Start the conversation!</p>
        </div>
      ) : (
        threadedComments.map((comment) => (
          <CommentThread key={comment.id} comment={comment} depth={0} />
        ))
      )}
      <div ref={messagesEndRef} />
    </div>
  );

  const renderErrorDisplay = (compactTouchLayout: boolean) => (
    <AnimatePresence>
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 ${
            compactTouchLayout ? 'mx-4 mb-3' : 'mx-4 mb-2'
          }`}
        >
          <p className="text-sm text-red-700">{error}</p>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderComposerSharedFields = (compactTouchLayout: boolean) => (
    <>
      <div className={`mb-3 flex border-b pb-2 ${
        compactTouchLayout
          ? 'flex-wrap items-center gap-2 border-gray-200 dark:border-gray-700'
          : 'items-center justify-between border-gray-100 dark:border-gray-700'
      }`}>
        <div className="flex items-center space-x-2">
          <button
            onClick={formatBold}
            className={`rounded p-1.5 transition-colors ${
              isDarkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="Bold (**text**)"
          >
            <Bold size={14} />
          </button>
          <button
            onClick={formatItalic}
            className={`rounded p-1.5 transition-colors ${
              isDarkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="Italic (*text*)"
          >
            <Italic size={14} />
          </button>
          <button
            onClick={formatQuote}
            className={`rounded p-1.5 transition-colors ${
              isDarkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="Quote (> text)"
          >
            <Quote size={14} />
          </button>
          <div className={`h-4 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
          <button
            onClick={() => setShowFormattingHelp(!showFormattingHelp)}
            className={`rounded p-1.5 transition-colors ${
              isDarkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="Markdown help"
          >
            <Type size={14} />
          </button>
        </div>
        <span className={`text-xs ${compactTouchLayout ? 'ml-auto' : ''} text-gray-500`}>
          Markdown supported
        </span>
      </div>

      <AnimatePresence>
        {showFormattingHelp && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`mb-3 rounded-lg p-3 text-xs ${
              isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}
          >
            <div className="grid grid-cols-2 gap-2">
              <div><code>**bold**</code> → <strong>bold</strong></div>
              <div><code>*italic*</code> → <em>italic</em></div>
              <div><code>`code`</code> → <code className={`rounded px-1 ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              }`}>code</code></div>
              <div><code>&gt; quote</code> → blockquote</div>
              <div><code># Heading</code> → heading</div>
              <div><code>- list</code> → bullet list</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {replyingTo && replyingToComment && (
        <div className={`mb-3 flex items-center justify-between rounded-xl p-2.5 ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <Reply size={14} className="shrink-0" />
            <span className={`truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Replying to {replyingToComment.userName || replyingToComment.userEmail}
            </span>
          </div>
          <button
            onClick={cancelReply}
            className={`shrink-0 text-xs ${
              isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Cancel
          </button>
        </div>
      )}

      {isPublicView && !user && (
        <div className="mb-3">
          <input
            type="text"
            value={anonName}
            onChange={(e) => setAnonName(e.target.value)}
            onFocus={() => {
              isComposerFocusedRef.current = true;
            }}
            onBlur={() => {
              isComposerFocusedRef.current = false;
            }}
            placeholder="Enter your name here"
            className={`w-full border text-base focus:border-transparent focus:ring-2 focus:ring-[#FFB52B]/35 ${
              compactTouchLayout ? 'rounded-2xl px-4 py-3' : 'rounded-lg p-2'
            } ${
              isDarkMode
                ? 'border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-400'
                : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
            }`}
            style={compactTouchLayout ? { fontSize: '16px' } : undefined}
            data-allow-touch-scroll="true"
            maxLength={50}
            disabled={isLoading}
          />
          <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Leave empty for a random guest name
          </p>
        </div>
      )}
    </>
  );

  const renderDesktopComposer = () => (
    <div className="shrink-0 border-t border-gray-200">
      <button
        onClick={() => setIsInputCollapsed(!isInputCollapsed)}
        className={`flex w-full items-center justify-center gap-2 p-2 transition-colors ${
          isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
        }`}
        title={isInputCollapsed ? 'Expand input area' : 'Collapse input area'}
      >
        {isInputCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        <span className="text-xs">{isInputCollapsed ? 'Show input' : 'Hide input'}</span>
      </button>

      {!isInputCollapsed && (
        <div className="p-4">
          {renderComposerSharedFields(false)}
          <div className="flex space-x-2">
            <textarea
              ref={textareaRef}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onFocus={() => {
                isComposerFocusedRef.current = true;
              }}
              onBlur={() => {
                isComposerFocusedRef.current = false;
              }}
              onKeyPress={handleKeyPress}
              placeholder={
                replyingTo
                  ? 'Write your reply... (Markdown supported)'
                  : isPublicView
                    ? 'Add a public comment... (Markdown supported)'
                    : 'Add a comment... (Markdown supported)'
              }
              className={`flex-1 resize-none rounded-lg border p-3 text-base focus:border-transparent focus:ring-2 focus:ring-[#FFB52B]/35 ${
                isDarkMode
                  ? 'border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-400'
                  : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
              } ${effectiveFullscreen ? 'min-h-[120px]' : 'min-h-[80px]'}`}
              data-allow-touch-scroll="true"
              rows={effectiveFullscreen ? 4 : 3}
              disabled={isLoading}
            />
            <button
              onClick={() => {
                console.log('[COMMENTS] Send button clicked', {
                  newComment: newComment.trim(),
                  isLoading,
                  disabled: !newComment.trim() || isLoading
                });
                addComment();
              }}
              disabled={!newComment.trim() || isLoading}
              className="flex items-center rounded-lg bg-[#FFB52B] px-4 py-3 text-[#232E3F] transition-colors hover:bg-[#FFC54D] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
          <div className="mt-2 flex justify-between text-xs text-gray-500">
            <span>Auto-refresh: {isIdle ? 'Paused (idle)' : 'Active'}</span>
            <span>Press Enter + Shift for new line, Enter to send</span>
          </div>
        </div>
      )}
    </div>
  );

  const renderMobileComposer = () => (
    <div className={`shrink-0 border-t ${isDarkMode ? 'border-gray-700 bg-[#16202c]' : 'border-gray-200 bg-white'}`}>
      <div
        className="px-4 pb-4 pt-3"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        {renderComposerSharedFields(true)}
        <div className="space-y-3">
          <textarea
            ref={textareaRef}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onFocus={() => {
              isComposerFocusedRef.current = true;
            }}
            onBlur={() => {
              isComposerFocusedRef.current = false;
            }}
            onKeyPress={handleKeyPress}
            placeholder={
              replyingTo
                ? 'Write your reply... (Markdown supported)'
                : isPublicView
                  ? 'Add a public comment... (Markdown supported)'
                  : 'Add a comment... (Markdown supported)'
            }
            className={`min-h-[120px] w-full resize-none rounded-2xl border px-4 py-3 text-base shadow-sm focus:border-transparent focus:ring-2 focus:ring-[#FFB52B]/35 ${
              isDarkMode
                ? 'border-gray-600 bg-gray-800 text-gray-100 placeholder-gray-400'
                : 'border-gray-300 bg-[#f8fafc] text-gray-900 placeholder-gray-500'
            }`}
            style={{ fontSize: '16px' }}
            data-allow-touch-scroll="true"
            rows={4}
            disabled={isLoading}
            enterKeyHint="send"
          />
          <button
            onClick={() => {
              console.log('[COMMENTS] Send button clicked', {
                newComment: newComment.trim(),
                isLoading,
                disabled: !newComment.trim() || isLoading
              });
              addComment();
            }}
            disabled={!newComment.trim() || isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFB52B] px-4 py-3.5 text-sm font-semibold text-[#232E3F] transition-colors hover:bg-[#FFC54D] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send size={16} />
            <span>{replyingTo ? 'Send reply' : 'Send comment'}</span>
          </button>
          <div className={`flex flex-col gap-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <span>Auto-refresh: {isIdle ? 'Paused (idle)' : 'Active'}</span>
            <span>Press Enter + Shift for new line, Enter to send</span>
          </div>
        </div>
      </div>
    </div>
  );

  const mobilePanelContent = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {renderPanelHeader(true)}
      {renderCommentsList(true)}
      {renderErrorDisplay(true)}
      {renderMobileComposer()}
    </div>
  );

  const desktopPanelContent = (
    <>
      {renderPanelHeader(false)}
      {renderCommentsList(false)}
      {renderErrorDisplay(false)}
      {renderDesktopComposer()}
    </>
  );

  return (
    <>

      {/* Comments Panel */}
      <AnimatePresence>
        {true && (
          isMobilePanel ? (
            <div
              className={`fixed inset-x-0 z-[90] flex min-h-0 flex-col ${
                isDarkMode ? 'bg-[#111826]' : 'bg-[#f4f4f4]'
              }`}
              data-allow-touch-scroll="true"
              style={{
                top: 'var(--app-mobile-top-offset)',
                bottom: 'var(--app-mobile-bottom-offset)',
                overscrollBehavior: 'contain',
              }}
            >
              {mobilePanelContent}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: isInline ? 0 : (effectiveFullscreen ? 0 : 400), scale: effectiveFullscreen ? 0.95 : 1 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: isInline ? 0 : (effectiveFullscreen ? 0 : 400), scale: effectiveFullscreen ? 0.95 : 1 }}
              className={`${isInline ? 'relative w-full' : 'fixed z-50'} bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 flex min-h-0 flex-col ${
                isInline
                  ? 'rounded-lg'
                  : effectiveFullscreen
                    ? 'inset-4 rounded-xl'
                    : 'w-[480px] rounded-lg shadow-2xl'
              }`}
              style={floatingPanelStyle}
              data-allow-touch-scroll="true"
            >
              {!isInline && !effectiveFullscreen && (
                <div
                  className={`absolute top-0 left-0 right-0 h-1 cursor-ns-resize z-40 ${
                    isDarkMode ? 'hover:bg-[#FFCF74]/70' : 'hover:bg-[#FFB52B]'
                  } ${isDragging ? 'bg-[#FFB52B]' : ''} transition-colors`}
                  onMouseDown={handleMouseDown}
                  title="Drag to resize panel height"
                />
              )}
              {desktopPanelContent}
            </motion.div>
          )
        )}
      </AnimatePresence>

      {/* Global function to add pinned messages */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            window.addPinnedMessage = function(originalMessage, model) {
              if (window.commentsRef && window.commentsRef.addPinnedMessage) {
                window.commentsRef.addPinnedMessage(originalMessage, model);
              }
            };
          `
        }}
      />
    </>
  );
};

// Export the addPinnedMessage function for use in other components
export const useCommentsActions = (sessionId: string | null, token: string | null) => {
  const { user } = useAuth();
  
  const addPinnedMessage = async (originalMessage: string, model: string) => {
    if (!sessionId || !token || !user) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: `Pinned message from ${model}`,
          type: 'pinned_message',
          pinnedContent: {
            originalMessage,
            model,
            timestamp: new Date().toISOString()
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to pin message');
      }

      return true;
    } catch (err) {
      console.error('Failed to pin message:', err);
      return false;
    }
  };

  return { addPinnedMessage };
};

const Comments = React.memo(CommentsComponent, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders from audio context updates
  // Only re-render if actual props change
  // Note: we're not comparing onCommentsChange and onClose callbacks to avoid
  // re-renders when parent components re-create these functions
  return (
    prevProps.sessionId === nextProps.sessionId &&
    prevProps.token === nextProps.token &&
    prevProps.isPublicView === nextProps.isPublicView &&
    prevProps.isInline === nextProps.isInline &&
    prevProps.pendingTimestamp === nextProps.pendingTimestamp &&
    prevProps.selectedCommentId === nextProps.selectedCommentId &&
    prevProps.disablePolling === nextProps.disablePolling &&
    prevProps.initialComments === nextProps.initialComments
  );
});

export default Comments;
